'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { usePathname } from 'next/navigation';
import { solicitudService } from '@/lib/api/solicitudService';
import { Solicitud } from '@/types/solicitud';
import { SolicitudTable } from './SolicitudTable';
import { SolicitudDetail } from './SolicitudDetail';
import { Button } from '@/components/ui/Button';
import ErrorMessage from '@/components/common/ErrorMessage';
import { useAuth } from '@/hooks/useAuth';
import { userService } from '@/lib/api/userService';
import { User } from '@/types/user';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { generateBankLayout, downloadTxtFile } from '@/utils/exportUtils';

// Icons
const SearchIcon = () => (
  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const DownloadIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

const FileIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const removeAccents = (str: string): string => {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

const FILTER_FIELDS = [
  { value: 'nombre', label: 'Nombre Completo' },
  { value: 'clienteEmpresa', label: 'Empresa' },
  { value: 'producto', label: 'Producto' },
  { value: 'asesorNombre', label: 'Asesor' },
  { value: 'solicitudEstatus', label: 'Estatus' },
  { value: 'solicitudNumeroControl', label: 'Número de Control' },
  { value: 'montoSolicitado', label: 'Monto Solicitado' },
  { value: 'montoAutorizado', label: 'Monto Autorizado' },
  { value: 'clienteNuevoRenovacion', label: 'Nuevo/Renovación' },
  { value: 'regionNombre', label: 'Región' },
];

const TransferenciasManagement: React.FC = () => {
  const { user: authUser } = useAuth();
  const pathname = usePathname();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [solicitudes, setSolicitudes] = useState<Solicitud[]>([]);
  const [filtered, setFiltered] = useState<Solicitud[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedSolicitud, setSelectedSolicitud] = useState<Solicitud | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterField, setFilterField] = useState<string>('nombre');
  const [filterValue, setFilterValue] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState(false);


  const authSub = authUser?.sub ?? null;

  const fetchSolicitudes = async () => {
    if (!authSub) return;
    try {
      setIsLoading(true);
      setError(null);
      const response = await solicitudService.getTransferencias(authSub);
      if (response.data) {
        setSolicitudes(response.data || []);
      } else {
        setError(response.error || 'Error al cargar las solicitudes');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar las solicitudes');
      if (process.env.NODE_ENV === 'development') console.error('Error fetching solicitudes:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!authSub) return;
    userService.getUser(authSub).then((response) => {
      if (response.data) setCurrentUser(response.data);
    }).catch((err) => {
      if (process.env.NODE_ENV === 'development') console.error('Error cargando usuario:', err);
    });
  }, [authSub]);

  useEffect(() => {
    if (!authSub) return;
    fetchSolicitudes();
  }, [authSub, pathname]);

  const getFieldValue = (solicitud: Solicitud, field: string): string => {
    if (field === 'nombre') {
      const nombre = solicitud.clienteNombre || '';
      const apellidoP = solicitud.clienteApellidoPaterno || '';
      const apellidoM = solicitud.clienteApellidoMaterno || '';
      return `${nombre} ${apellidoP} ${apellidoM}`.trim();
    }
    const value = (solicitud as any)[field];
    return value !== null && value !== undefined ? String(value) : '';
  };

  const matchesFilter = (solicitud: Solicitud, field: string, value: string): boolean => {
    const fieldValue = getFieldValue(solicitud, field);
    if (value === '*') return fieldValue.trim() !== '';
    if (value === '""' || value === '') return fieldValue.trim() === '';
    const normalizedValue = removeAccents(value.toLowerCase().trim());
    const normalizedFieldValue = removeAccents(fieldValue.toLowerCase());
    return normalizedFieldValue.includes(normalizedValue);
  };

  // Lista ya viene filtrada y ordenada del backend (FIRMAR CONTRATO + contrato Ok + scope)
  const baseList = useMemo(() => {
    let list = solicitudes;
    if (filterValue.trim() !== '') {
      list = list.filter(s => matchesFilter(s, filterField, filterValue));
    }
    return list;
  }, [solicitudes, filterField, filterValue]);

  // Aplicar búsqueda por texto libre
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFiltered(baseList);
    } else {
      const term = removeAccents(searchTerm.toLowerCase());
      const result = baseList.filter(s => {
        const nombreCompleto = removeAccents(
          `${s.clienteNombre || ''} ${s.clienteApellidoPaterno || ''} ${s.clienteApellidoMaterno || ''}`.trim().toLowerCase()
        );
        const empresa = removeAccents((s.clienteEmpresa || '').toLowerCase());
        const control = removeAccents((s.solicitudNumeroControl || '').toLowerCase());
        const producto = removeAccents((s.producto || '').toLowerCase());
        const asesor = removeAccents((s.asesorNombre || '').toLowerCase());
        return nombreCompleto.includes(term) || empresa.includes(term) || control.includes(term) || producto.includes(term) || asesor.includes(term);
      });
      setFiltered(result);
    }
  }, [searchTerm, baseList]);

  const handleEdit = (solicitud: Solicitud) => {
    if (!solicitud.id) return;
    setError(null);
    setSelectedSolicitud(solicitud);
    setIsDetailOpen(true);
  };

  const handleUpdateDetail = (updated: Solicitud) => {
    setSelectedSolicitud(updated);
    fetchSolicitudes();
  };

  // Función para escapar valores CSV (maneja comillas y comas)
  const escapeCSV = (value: any): string => {
    if (value === null || value === undefined) return '';
    const str = String(value);
    // Si contiene comillas, comas o saltos de línea, envolver en comillas y escapar comillas internas
    if (str.includes('"') || str.includes(',') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  // Función para extraer meses del plazo autorizado
  const extractMeses = (plazo: string | undefined): string => {
    if (!plazo) return '';
    // Buscar números en el string (ej: "12 meses", "12M", "12")
    const match = plazo.match(/(\d+)/);
    return match ? match[1] : '';
  };

  // Generar CSV de transferencias
  const generarReporteCSV = () => {
    setIsGenerating(true);

    if (filtered.length === 0) {
      alert("No hay solicitudes para generar reporte.");
      setIsGenerating(false);
      return;
    }

    // Definir encabezados
    const headers = [
      'Id',
      'Solicitud Numero De Control',
      'Cliente Apellido Materno',
      'Cliente Apellido Paterno',
      'Cliente Empresa',
      'Cliente Nombre',
      'Monto Autorizado ($)',
      'Monto Transferencia ($)',
      'Plazo Autorizado En Meses',
      'Producto',
      'Solicitud Destino Del Crédito',
      'Solicitud Mop Más Alto',
      'Tasa Autorizada (%)',
      'Tasa Comisión Autorizada (%)',
      'Analisis Realizado Por'
    ];

    // Crear filas de datos
    const rows = filtered.map((s) => {
      // Formatear tasas como porcentajes
      const tasaAutorizada = s.tasaAutorizada ? Number(s.tasaAutorizada).toFixed(2) : '0.00';
      const tasaComision = s.tasaDeComision ? Number(s.tasaDeComision).toFixed(2) : (s.tasaDeComisionPolitica ? Number(s.tasaDeComisionPolitica).toFixed(2) : '0.00');
      
      // Transformar producto: "Nómina" → "Crédito de Nómina"
      const productoCSV = s.producto === 'Nómina' ? 'Crédito de Nómina' : (s.producto || '');

      return [
        escapeCSV(s.id || ''),
        escapeCSV(s.solicitudNumeroControl || ''),
        escapeCSV(s.clienteApellidoMaterno || ''),
        escapeCSV(s.clienteApellidoPaterno || ''),
        escapeCSV(s.clienteEmpresa || ''),
        escapeCSV(s.clienteNombre || ''),
        escapeCSV(s.montoAutorizado || 0),
        escapeCSV(s.montoTransferencia || 0),
        escapeCSV(extractMeses(s.plazoAutorizado)),
        escapeCSV(productoCSV),
        escapeCSV(s.solicitudDestinoCredito || ''),
        escapeCSV(s.solicitudMop || ''),
        escapeCSV(tasaAutorizada),
        escapeCSV(tasaComision),
        escapeCSV(s.analistaNombre || s.usuarioAutorizacionRiesgos || '')
      ];
    });

    // Combinar encabezados y filas
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Agregar BOM para UTF-8 (ayuda con Excel)
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `transferencias_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    setIsGenerating(false);
  };

  // Función para obtener el color del badge según el estado del contrato
  const getContratoBadgeVariant = (contratoDocumento: string | undefined): "success" | "danger" | "warning" => {
    const estado = (contratoDocumento || "").trim();
    if (estado === "Ok") return "success";
    if (estado === "No Cumple") return "danger";
    return "warning";
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Transferencias</h1>
          <p className="text-sm text-gray-500 mt-1">
            Solicitudes con estatus FIRMAR CONTRATO y contrato validado (Ok) listas para transferir
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={fetchSolicitudes} disabled={isLoading}>
            {isLoading ? "Cargando…" : "Recargar"}
          </Button>
          <button
            onClick={generarReporteCSV}
            disabled={isGenerating || filtered.length === 0}
            className={`
              inline-flex items-center justify-center gap-2.5 px-6 py-3
              font-semibold text-white rounded-lg
              bg-gradient-to-r from-blue-600 to-indigo-600
              hover:from-blue-700 hover:to-indigo-700
              active:from-blue-800 active:to-indigo-800
              shadow-lg hover:shadow-xl
              transform transition-all duration-200
              hover:scale-105 active:scale-100
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
              disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
              ${isGenerating ? 'cursor-wait' : ''}
            `}
          >
            {isGenerating ? (
              <>
                <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Generando...</span>
              </>
            ) : (
              <>
                <DownloadIcon className="w-5 h-5" />
                <span>Descargar CSV</span>
                <FileIcon className="w-4 h-4 opacity-80" />
              </>
            )}
          </button>
        </div>
      </div>

      {/* Filtros Avanzados */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end mb-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Campo</label>
            <select
              value={filterField}
              onChange={(e) => {
                setFilterField(e.target.value);
                setFilterValue('');
              }}
              className="w-full px-3 py-2 border rounded-lg shadow-sm border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            >
              {FILTER_FIELDS.map(f => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Valor</label>
            <Input
              value={filterValue}
              onChange={(e) => setFilterValue(e.target.value)}
              placeholder='Use * para campo lleno, "" para vacío'
            />
          </div>
          <div>
            <Button variant="secondary" onClick={() => setFilterValue('')}>
              Limpiar Filtro
            </Button>
          </div>
        </div>

        {/* Búsqueda General */}
        <div className="flex flex-col md:flex-row gap-3 items-start md:items-center justify-between">
          <div className="w-full md:w-2/3">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SearchIcon />
              </div>
              <input
                type="text"
                placeholder="Buscar por nombre completo, empresa, control, producto o asesor (sin acentos)…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-all"
              />
            </div>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
          <div>
            Mostrando <span className="font-medium text-gray-700">{filtered.length}</span> de{" "}
            <span className="font-medium text-gray-700">{baseList.length}</span> solicitudes
          </div>
          {filterValue && (
            <div className="text-gray-400">
              Filtro: {FILTER_FIELDS.find(f => f.value === filterField)?.label} = {filterValue}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex justify-center items-center h-64 bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600" />
        </div>
      ) : error ? (
        <ErrorMessage message={error} onRetry={fetchSolicitudes} />
      ) : (
        <>
          {filtered.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-10 text-center">
              <div className="text-sm font-semibold text-gray-900">Sin solicitudes para transferir</div>
              <div className="mt-1 text-sm text-gray-500">
                No hay solicitudes con estatus FIRMAR CONTRATO y contrato validado (Ok). Ajusta los filtros.
              </div>
              <div className="mt-4 flex justify-center gap-2">
                <Button variant="secondary" onClick={() => setSearchTerm("")}>
                  Limpiar búsqueda
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Tabla con indicadores visuales */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <SolicitudTable
                  solicitudes={filtered}
                  onEdit={handleEdit}
                  montoColumn="autorizado"
                  rowClassName={(s) => (s.contratoDocumento || '').trim() === 'No Cumple' ? 'bg-red-50 border-l-4 border-red-500 text-red-700' : undefined}
                  renderActions={(s) => (
                    <div className="flex items-center gap-2">
                      <Badge variant={getContratoBadgeVariant(s.contratoDocumento)}>
                        {s.contratoDocumento || 'Pendiente'}
                      </Badge>
                    </div>
                  )}
                />
              </div>
              <div className="text-right text-xs text-gray-400 px-2">
                Total: {filtered.length} solicitudes listas para transferir
              </div>
            </>
          )}

          <SolicitudDetail
            isOpen={isDetailOpen}
            onClose={() => setIsDetailOpen(false)}
            solicitud={selectedSolicitud}
            onUpdate={handleUpdateDetail}
            modulo="transferencias"
          />
        </>
      )}
    </div>
  );
};

export default TransferenciasManagement;
